import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { cn } from "@/lib/utils";

/** Shrink until text fits width; returns the size that fits. */
function measureFitPx(el: HTMLElement, maxPx: number, minPx: number): number {
  let size = maxPx;
  el.style.fontSize = `${size}px`;
  while (size > minPx && el.scrollWidth > el.clientWidth + 0.5) {
    size -= 0.5;
    el.style.fontSize = `${size}px`;
  }
  return size;
}

type FitTextGroupApi = {
  register: (id: string, el: HTMLElement) => () => void;
  fitAll: () => void;
};

const FitTextGroupContext = createContext<FitTextGroupApi | null>(null);

interface FitTextGroupProps {
  children: React.ReactNode;
  className?: string;
  /** Starting font size in px for every member. */
  maxPx?: number;
  /** Smallest font size in px before giving up. */
  minPx?: number;
}

/**
 * Measures each FitText child, then applies the smallest fitting font size
 * to every member so the set stays uniform.
 */
export const FitTextGroup: React.FC<FitTextGroupProps> = ({
  children,
  className,
  maxPx = 14,
  minPx = 8,
}) => {
  const membersRef = useRef(new Map<string, HTMLElement>());
  const containerRef = useRef<HTMLDivElement>(null);
  const boundsRef = useRef({ maxPx, minPx });
  boundsRef.current = { maxPx, minPx };

  const fitAll = useCallback(() => {
    const { maxPx: max, minPx: min } = boundsRef.current;
    const els = [...membersRef.current.values()];
    if (els.length === 0) return;

    let shared = max;
    for (const el of els) {
      shared = Math.min(shared, measureFitPx(el, max, min));
    }
    const next = `${shared}px`;
    for (const el of els) {
      if (el.style.fontSize !== next) el.style.fontSize = next;
    }
  }, []);

  const register = useCallback((id: string, el: HTMLElement) => {
    membersRef.current.set(id, el);
    return () => {
      membersRef.current.delete(id);
    };
  }, []);

  const api = useMemo(() => ({ register, fitAll }), [register, fitAll]);

  useLayoutEffect(() => {
    fitAll();
    const root = containerRef.current;
    if (!root) return;
    const ro = new ResizeObserver(fitAll);
    ro.observe(root);
    return () => ro.disconnect();
  }, [fitAll, children, maxPx, minPx]);

  return (
    <FitTextGroupContext.Provider value={api}>
      <div ref={containerRef} className={className}>
        {children}
      </div>
    </FitTextGroupContext.Provider>
  );
};

interface FitTextProps {
  children: React.ReactNode;
  className?: string;
  /** Starting font size in px. Ignored inside FitTextGroup (group owns bounds). */
  maxPx?: number;
  /** Smallest font size in px before giving up. Ignored inside FitTextGroup. */
  minPx?: number;
}

/**
 * Shrinks font-size until the text fits the element's width (no ellipsis).
 * Parent must constrain width (e.g. flex child with min-w-0 / overflow-hidden).
 * Inside FitTextGroup, all siblings share the smallest fitting size.
 */
const FitText: React.FC<FitTextProps> = ({ children, className, maxPx = 14, minPx = 8 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const group = useContext(FitTextGroupContext);
  const id = useId();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (group) {
      const unregister = group.register(id, el);
      group.fitAll();
      const ro = new ResizeObserver(group.fitAll);
      ro.observe(el);
      if (el.parentElement) ro.observe(el.parentElement);
      return () => {
        unregister();
        ro.disconnect();
        group.fitAll();
      };
    }

    const fit = () => {
      measureFitPx(el, maxPx, minPx);
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [children, maxPx, minPx, group, id]);

  return (
    <span ref={ref} className={cn("block w-full whitespace-nowrap", className)}>
      {children}
    </span>
  );
};

export default FitText;
